import { useCallback, useMemo, type FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { PageContextWrapper } from "@ryde/controls";
import { useSelectedIds, useYupValidationResolver } from "@ryde/redux";
import {
  BOOKING_DATE_TIME_UPDATE_FORM_FIELDS,
  BOOKING_DATE_TIME_UPDATE_PANEL_LABEL_KEYS,
  bookingBulkCreateStateFromPollingState,
  DirectionEnum,
  isBulkBookingSetAbortedSelector,
  useTimezone,
  type IBulkUpdateBookingsDateTimeDto,
  type IBulkUpdateBookingsDateTimeForm,
  type IBulkUpdateBookingsDateTimeProps,
  type IInjectedDrawerProps,
} from "@ryde/shared";

import { useBookingsPollingHandlingActions } from "../../../../services";
import { CommonDefaultDrawer } from "../../common-default-drawer";
import { BaseDrawer } from "../../drawer";
import {
  buildBulkUpdateBookingsDateTimePayload,
  BulkCreateBookingFooter,
  getApprovalBookingConflictAPI,
  getBookingIdsForDirection,
  PreviewStepForm,
  useBulkBookingFlow,
  useSelectedBulkBookings,
  type IUseBulkBookingGetPayloadParams,
} from "../bulk-bookings-components";
import { BulkUpdateBookingsDateTimeForm } from "./bulk-update-bookings-date-time-form";
import { bulkUpdateBookingsDateTimeValidationSchema } from "./bulk-update-bookings-date-time.validation";

export const BulkUpdateBookingsDateTimeSuspended: FC<
  IBulkUpdateBookingsDateTimeProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const { clearSelectedIds } = useSelectedIds("booking");
  const { initBookingsDateTimeUpdatePollingHandler } =
    useBookingsPollingHandlingActions();
  const resolver = useYupValidationResolver(
    bulkUpdateBookingsDateTimeValidationSchema,
    "booking",
  );

  const selectedBookings = useSelectedBulkBookings();
  const isBulkBookingSetAborted = useRecoilValue(
    isBulkBookingSetAbortedSelector,
  );
  const {
    newBookingsIds: updatedBookingIds,
    isLoading: isBulkBookingSetProceeding,
  } = useRecoilValue(bookingBulkCreateStateFromPollingState);

  const methods = useForm<IBulkUpdateBookingsDateTimeForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      direction: DirectionEnum.PickUp,
      date: null,
    },
    resolver,
  });
  const {
    getValues,
    trigger,
    watch,
    formState: { isDirty },
  } = methods;

  const direction = watch(BOOKING_DATE_TIME_UPDATE_FORM_FIELDS.direction);
  const bookingCount = useMemo(
    () =>
      getBookingIdsForDirection({ bookings: selectedBookings, direction })
        .length,
    [direction, selectedBookings],
  );

  const getPayload = useCallback(
    ({
      isPreview,
      approvalConflicts,
    }: IUseBulkBookingGetPayloadParams): IBulkUpdateBookingsDateTimeDto => {
      const formValues = getValues();

      return buildBulkUpdateBookingsDateTimePayload({
        // Narrowed from the same form snapshot the payload names its direction from, so the run
        // cannot be sent one direction's ids under the other direction's name.
        bookingIds: getBookingIdsForDirection({
          bookings: selectedBookings,
          direction: formValues.direction,
        }),
        formValues,
        isPreview,
        approvalBookingConflicts:
          getApprovalBookingConflictAPI(approvalConflicts),
        timezone,
      });
    },
    [getValues, selectedBookings, timezone],
  );

  const handleCloseAndClearIdsDrawer = useEvent((): void => {
    // Closing without having changed anything leaves the selection alone, so the operator can
    // reopen the panel or reach for another bulk action.
    if (updatedBookingIds?.length) {
      clearSelectedIds();
    }

    onCloseDrawer?.();
  });

  const {
    onCloseClick,
    saveLabel,
    onSaveClick,
    cancelLabel,
    onCancelClick,
    isPreviewStep,
    refreshHandler,
    abortHandler,
    unapprovedConflictsCount,
    labelKeys,
  } = useBulkBookingFlow<
    IBulkUpdateBookingsDateTimeForm,
    IBulkUpdateBookingsDateTimeDto
  >({
    onCloseDrawer: handleCloseAndClearIdsDrawer,
    getPayload,
    startPolling: initBookingsDateTimeUpdatePollingHandler,
    labelKeys: BOOKING_DATE_TIME_UPDATE_PANEL_LABEL_KEYS,
    isDirty,
    trigger,
  });
  const approvedCount = bookingCount - unapprovedConflictsCount;

  const isSaveButtonDisabled =
    isBulkBookingSetAborted ||
    // Nothing of the chosen direction is selected: starting a run would only land the panel on an
    // empty preview it cannot move on from.
    bookingCount <= 0 ||
    (isPreviewStep && (isBulkBookingSetProceeding || approvedCount <= 0));

  return (
    <BaseDrawer onCloseDrawer={onCloseClick} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("booking.bulk.bookingsDateTimeEdit")}
        onCloseDrawer={onCloseClick}
        Footer={
          <BulkCreateBookingFooter
            saveLabel={saveLabel}
            onSaveClick={onSaveClick}
            cancelLabel={cancelLabel}
            onCancelClick={onCancelClick}
            isSaveDisabled={isSaveButtonDisabled}
            isLoading={isBulkBookingSetProceeding}
          />
        }
      >
        <PageContextWrapper entity="booking">
          <FormProvider {...methods}>
            {isPreviewStep ? (
              <PreviewStepForm
                onSaveClick={onSaveClick}
                bookingCount={bookingCount}
                onRefresh={refreshHandler}
                onAbort={abortHandler}
                successfullyBooked={approvedCount}
                labelKeys={labelKeys}
                isGapConflictApprovable
              />
            ) : (
              <BulkUpdateBookingsDateTimeForm
                direction={direction}
                matchingCount={bookingCount}
                selectedCount={selectedBookings.length}
              />
            )}
          </FormProvider>
        </PageContextWrapper>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
